import { Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { CaseStudyTOC } from "@/components/caseStudy/CaseStudyTOC";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { MetricCard } from "@/components/metrics/MetricCard";
import { getProject } from "@/content/portfolio";
import { SERVICES } from "@/content/services";
import { INDUSTRIES } from "@/content/industries";
import { industryVariantKey } from "@/content/imagery";
import { Picture } from "@/components/media/Picture";
import { BrandFigure, figuresForSlug } from "@/components/media/BrandFigure";
import type { IndustryPage } from "@/content/industries";
import { getAustinAngle } from "@/content/industries";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { SiloDiscovery } from "@/components/shared/SiloDiscovery";
import { siteConfig } from "@/config/site";


export function IndustryPageTemplate({ industry }: { industry: IndustryPage }) {
  const hasProof = industry.proofSlugs.length > 0;
  const austin = getAustinAngle(industry.slug);
  const toc = [
    { id: "overview", label: "01 · Market context" },
    ...(austin ? [{ id: "austin", label: "02 · Austin angle" }] : []),
    { id: "economics", label: `${austin ? "03" : "02"} · Economics` },
    { id: "journey", label: `${austin ? "04" : "03"} · Buyer journey` },
    { id: "playbook", label: `${austin ? "05" : "04"} · Playbook` },
    { id: "framework", label: `${austin ? "06" : "05"} · Framework` },
    { id: "kpis", label: `${austin ? "07" : "06"} · KPIs` },
    { id: "timeline", label: `${austin ? "08" : "07"} · Timeline` },
    { id: "traps", label: `${austin ? "09" : "08"} · Traps` },
    { id: "fit", label: `${austin ? "10" : "09"} · Fit` },
    ...(hasProof ? [{ id: "proof", label: `${austin ? "11" : "10"} · Proof` }] : []),
    { id: "services", label: `${austin ? (hasProof ? "12" : "11") : (hasProof ? "11" : "10")} · Services` },
    { id: "faq", label: `${austin ? (hasProof ? "13" : "12") : (hasProof ? "12" : "11")} · FAQ` },
  ];

  const proofProjects = industry.proofSlugs
    .map(getProject)
    .filter(Boolean) as NonNullable<ReturnType<typeof getProject>>[];

  const relatedServices = industry.relatedServices
    .map((slug) => SERVICES.find((s) => s.slug === slug))
    .filter(Boolean) as (typeof SERVICES)[number][];

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        {/* HERO */}
        <section className="relative overflow-hidden border-b border-border">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{ background: "var(--gradient-hero)" }}
          />
          <div className="container-page relative pt-10 pb-12 md:pt-14 md:pb-16">
            <Breadcrumbs 
              items={[
                { name: "Industries", path: "/industries/" },
                { name: industry.name, path: `/industries/${industry.slug}/` }
              ]} 
            />
            <div className="mt-4 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-end">
              <div>
                <EyebrowLabel>{industry.eyebrow}</EyebrowLabel>
                <h1 className="mt-5 font-display text-5xl md:text-7xl leading-[1.02] text-balance">
                  {industry.hero.headline}
                </h1>
                <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
                  {industry.hero.deck}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/contact/"
                    search={{ location: "", service: industry.name }}
                    className="inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
                  >
                    Book a strategy call →
                  </Link>
                  <a
                    href="#playbook"
                    className="inline-flex rounded-full border border-border-strong px-6 py-3 text-sm hover:border-primary hover:text-primary"
                  >
                    See the playbook
                  </a>
                </div>
              </div>
              <aside className="space-y-4">
                <div className="rounded-2xl overflow-hidden border border-border bg-surface-2">
                  <Picture
                    variantKey={industryVariantKey(industry.slug)}
                    alt=""
                    width={1024}
                    height={640}
                    priority
                    sizes="(min-width: 1024px) 400px, 100vw"
                  />
                </div>
                <div className="surface-card p-6">
                  <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                    At a glance
                  </div>
                  <ul className="mt-4 space-y-3">
                    {industry.tldr.map((t) => (
                      <li key={t} className="flex items-start gap-3 text-sm">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="container-page py-16 grid gap-12 lg:grid-cols-[220px_1fr] items-start">
          <CaseStudyTOC items={toc} />

          <article className="max-w-3xl min-w-0">
            {/* Market context */}
            <section id="overview" className="scroll-mt-24">
              <EyebrowLabel>Market context</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight">
                Where {industry.name.toLowerCase()} search stands in 2026.
              </h2>
              <div className="mt-6 space-y-5">
                {industry.marketContext.map((p, i) => (
                  <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90">
                    {p}
                  </p>
                ))}
              </div>
              <BrandFigure
                className="mt-8"
                kind={figuresForSlug(industry.slug, 3)[0]}
                title={`${industry.name}: where demand concentrates in search today`}
              />
            </section>

            {/* Austin & Central Texas angle (Phase 4 localization) */}
            {austin && (
              <section id="austin" className="scroll-mt-24 mt-16 border-t border-border pt-12">
                <EyebrowLabel>{siteConfig.contact.address.city} & Central {siteConfig.contact.address.state} angle</EyebrowLabel>
                <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight">
                  {austin.headline}
                </h2>
                <p className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-5 text-sm md:text-base leading-relaxed text-foreground/90">
                  <span className="mr-2 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-widest text-primary">
                    Founder-led
                  </span>
                  {austin.founderNote}
                </p>

                <div className="mt-6 space-y-5">
                  {austin.paragraphs.map((p, i) => (
                    <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90">
                      {p}
                    </p>
                  ))}
                </div>
                <div className="mt-8 grid gap-6 md:grid-cols-2">
                  <div className="surface-card p-6">
                    <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                      Entities we cover
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-foreground/90">
                      {austin.entities.map((e) => (
                        <li key={e} className="flex gap-2">
                          <span aria-hidden className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                          <span>{e}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="surface-card p-6">
                    <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                      Service area for this vertical
                    </div>
                    <ul className="mt-3 flex flex-wrap gap-2 text-xs">
                      {austin.serviceAreas.map((c) => (
                        <li
                          key={c}
                          className="rounded-full border border-border-strong px-3 py-1 text-foreground/80"
                        >
                          {c}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 text-xs text-muted-foreground">
                      Programme direction from our {siteConfig.contact.address.city}, {siteConfig.contact.address.state} {siteConfig.contact.address.zip} HQ · {siteConfig.contact.phone} · {siteConfig.contact.email}
                    </p>

                  </div>
                </div>
              </section>
            )}

            {/* Economics */}
            <section id="economics" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Vertical economics</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                The numbers that shape the strategy.
              </h2>
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {industry.economics.map((e) => (
                  <div key={e.label} className="surface-card p-6">
                    <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                      {e.label}
                    </div>
                    <div className="mt-2 font-display text-3xl text-primary">{e.value}</div>
                    <div className="mt-2 text-xs text-muted-foreground">{e.note}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Buyer journey */}
            <section id="journey" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Buyer journey</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                How your buyer actually searches.
              </h2>
              <div className="mt-8 space-y-6">
                {industry.buyerJourney.map((s, i) => (
                  <div key={s.stage} className="surface-card p-6 md:p-7">
                    <div className="flex flex-wrap items-baseline gap-4">
                      <div className="font-display text-2xl text-primary">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <h3 className="font-display text-xl md:text-2xl">{s.stage}</h3>
                      <span className="text-xs uppercase tracking-widest text-muted-foreground">
                        {s.intent}
                      </span>
                    </div>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <div>
                        <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                          Example queries
                        </div>
                        <ul className="mt-2 space-y-1.5">
                          {s.queries.map((q) => (
                            <li key={q} className="text-sm text-foreground/90">
                              "{q}"
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                          Our move
                        </div>
                        <p className="mt-2 text-sm text-foreground/90">{s.move}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <BrandFigure
                className="mt-8"
                kind="funnel"
                title={`${industry.name} buyer journey — from impression to revenue`}
              />
            </section>

            {/* Playbook */}
            <section id="playbook" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>The playbook</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                What we actually build for {industry.name.toLowerCase()}.
              </h2>
              <div className="mt-8 space-y-6">
                {industry.playbook.map((p, i) => (
                  <div key={p.title} className="surface-card p-6 md:p-7">
                    <div className="flex items-baseline gap-4">
                      <div className="font-display text-2xl text-primary">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <h3 className="font-display text-xl md:text-2xl">{p.title}</h3>
                    </div>
                    <p className="mt-3 text-foreground/90 leading-relaxed">{p.body}</p>
                    <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                      {p.outputs.map((o) => (
                        <li key={o} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Framework */}
            <section id="framework" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>{industry.framework.name}</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Our proprietary approach.
              </h2>
              <div className="mt-6 space-y-5">
                {industry.framework.body.map((p, i) => (
                  <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90">
                    {p}
                  </p>
                ))}
              </div>
              <ol className="mt-8 grid gap-4 sm:grid-cols-2">
                {industry.framework.steps.map((s, i) => (
                  <li key={s.label} className="surface-card p-5">
                    <div className="text-[0.7rem] uppercase tracking-widest text-primary">
                      Step {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-2 font-display text-xl">{s.label}</div>
                    <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
                  </li>
                ))}
              </ol>
            </section>

            {/* KPIs */}
            <section id="kpis" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>KPIs we commit to</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                The scoreboard.
              </h2>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {industry.kpis.map((k) => (
                  <div key={k.label} className="surface-card p-6">
                    <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                      {k.label}
                    </div>
                    <div className="mt-2 font-display text-2xl text-primary">{k.target}</div>
                    <div className="mt-2 text-xs text-muted-foreground">{k.note}</div>
                  </div>
                ))}
              </div>
              <BrandFigure
                className="mt-8"
                kind={figuresForSlug(industry.slug, 3)[2]}
                title={`${industry.name} scoreboard — the metrics we report against`}
              />
            </section>

            {/* Timeline */}
            <section id="timeline" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Timeline</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">A staged roadmap.</h2>
              <ol className="mt-8 relative border-l border-border pl-6 space-y-8">
                {industry.timeline.map((phase) => (
                  <li key={phase.phase}>
                    <div className="absolute -left-[7px] h-3 w-3 rounded-full bg-primary" />
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {phase.when}
                    </div>
                    <h3 className="mt-1 font-display text-xl">{phase.phase}</h3>
                    <ul className="mt-3 space-y-2">
                      {phase.work.map((w) => (
                        <li key={w} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            </section>

            {/* Traps */}
            <section id="traps" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Competitive traps</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Where {industry.name.toLowerCase()} campaigns usually fail.
              </h2>
              <div className="mt-8 space-y-6">
                {industry.competitiveTraps.map((t) => (
                  <div key={t} className="surface-card p-6 md:p-7 border-l-4 border-l-primary">
                    <p className="text-foreground/90 leading-relaxed">{t}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Silo reinforcement */}
            <SiloDiscovery 
              eyebrow="Industry connectivity"
              heading={`Scaling your ${industry.name} presence.`}
              intro={`Our ${industry.name} framework is built to be combined with technical excellence and local market dominance. These are the connected pillars of a successful Central Texas engagement.`}
              links={[
                {
                  to: "/services/seo/",
                  anchor: "Core SEO & AI search services",
                  note: "The technical and content engine that powers the industry playbook."
                },
                {
                  to: "/industries/",
                  anchor: "Browse all industry verticals",
                  note: "See how our cross-vertical experience informs your strategy."
                },
                {
                  to: `/seo-agency-${siteConfig.contact.address.city.toLowerCase()}/`,
                  anchor: `${siteConfig.contact.address.city} Search Agency HQ`,
                  note: "Direct access to the team and tools driving your industry growth."
                },
                {
                  to: "/portfolio/",
                  anchor: "Industry Case Studies",
                  note: "Verified outcomes from engagements in similar competitive sets."
                }
              ]}
            />

            {/* Fit */}
            <section id="fit" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Is this for you?</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                We turn away work that isn't a fit.
              </h2>
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                <div className="surface-card p-6">
                  <div className="text-primary text-xs uppercase tracking-widest">
                    This is for you if
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {industry.fitFor.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="surface-card p-6">
                  <div className="text-muted-foreground text-xs uppercase tracking-widest">
                    Not a fit if
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {industry.notFor.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Proof */}
            {hasProof && (
              <section id="proof" className="scroll-mt-24 mt-16 border-t border-border pt-12">
                <EyebrowLabel>Proof</EyebrowLabel>
                <h2 className="mt-4 font-display text-3xl md:text-4xl">
                  Engagements in this vertical.
                </h2>
                <div className="mt-8 grid gap-6">
                  {proofProjects.map((p) => (
                    <Link
                      key={p.slug}
                      to="/portfolio/$slug/"
                      params={{ slug: p.slug }}
                      className="surface-card p-6 grid gap-4 sm:grid-cols-[1fr_auto] items-center hover:-translate-y-0.5 transition-transform"
                    >
                      <div>
                        <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                          {p.industry} · {p.window}
                        </div>
                        <div className="mt-1 font-display text-xl">{p.client}</div>
                        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                          {p.tagline}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-3xl text-primary">
                          {p.metrics[0].value}
                        </div>
                        <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">
                          {p.metrics[0].label}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                {proofProjects[0] && (
                  <div className="mt-8 grid gap-4 sm:grid-cols-2">
                    {proofProjects[0].metrics.slice(0, 4).map((m) => (
                      <MetricCard key={m.label} metric={m} />
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Services */}
            <section id="services" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Services we deploy here</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                The disciplines behind the programme.
              </h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {relatedServices.map((s) => (
                  <Link
                    key={s.slug}
                    to="/services/$slug/"
                    params={{ slug: s.slug }}
                    className="surface-card p-5 hover:-translate-y-0.5 transition-transform"
                  >
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {s.category}
                    </div>
                    <div className="mt-1 font-display text-lg">{s.title}</div>
                    <div className="mt-2 text-sm text-muted-foreground line-clamp-2">
                      {s.metaDescription}
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Related industries — cross-link strip for topical relevance */}
            <section id="related-industries" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Related industries</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Adjacent verticals we also run programmes for.
              </h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
                Search patterns, buyer economics, and Local Pack behaviour often overlap. If your business
                straddles more than one vertical, these are the neighbouring playbooks worth reading.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {INDUSTRIES.filter((x) => x.slug !== industry.slug).slice(0, 4).map((x) => (
                  <Link
                    key={x.slug}
                    to="/industries/$slug/"
                    params={{ slug: x.slug }}
                    className="surface-card p-5 hover:-translate-y-0.5 transition-transform"
                  >
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {x.eyebrow}
                    </div>
                    <div className="mt-1 font-display text-lg">{x.name} SEO — Austin</div>
                    <div className="mt-2 text-sm text-muted-foreground line-clamp-2">
                      {x.hero.deck}
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-6">
                <Link to="/industries/" className="text-sm text-primary story-link">
                  See all industries →
                </Link>
              </div>
            </section>


            {/* FAQ */}
            <section id="faq" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>FAQ</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Questions we get on every {industry.name.toLowerCase()} call.
              </h2>
              <div className="mt-8 space-y-3">
                {industry.faqs.map((f) => (
                  <details
                    key={f.q}
                    className="surface-card p-5 group open:ring-1 open:ring-primary/30"
                  >
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                      <span className="font-medium text-foreground">{f.q}</span>
                      <span className="text-primary text-xl leading-none transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                      {f.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="mt-16">
              <div className="surface-card p-8 md:p-10">
                <EyebrowLabel>Next step</EyebrowLabel>
                <h2 className="mt-3 font-display text-3xl md:text-4xl">
                  Ready to run a proper {industry.name.toLowerCase()} programme?
                </h2>
                <p className="mt-3 text-muted-foreground max-w-xl">
                  Book a free strategy call. We'll audit your current footprint and tell
                  you honestly whether we're the right fit for the vertical.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/contact/"
                    search={(prev) => ({ ...prev, location: "", service: "" })}
                    className="inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
                  >
                    Book a strategy call →
                  </Link>
                  <Link
                    to="/industries/"
                    className="inline-flex rounded-full border border-border-strong px-6 py-3 text-sm hover:border-primary hover:text-primary"
                  >
                    All industries
                  </Link>
                </div>
              </div>
            </section>
          </article>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
